import type { EmblaCarouselType } from 'embla-carousel'
import EmblaCarousel from 'embla-carousel'

/**
 * Интерфейс состояния слайдера.
 * Содержит ссылки на DOM-элемент, экземпляр слайдера, текущий индекс и доступные направления навигации.
 */
interface SliderState {
  el: HTMLElement
  slider: EmblaCarouselType
  activeSlideIndex: number
  availableDirections: {
    prev: boolean
    next: boolean
  }
}

/**
 * Обновляет видимость кнопок навигации слайдера в зависимости от доступных направлений.
 * Добавляет или удаляет CSS-класс `quest-slider__button_hidden` для кнопок "prev" и "next".
 * @param {SliderState} state - Состояние слайдера с информацией о доступных направлениях
 */
function updateButtonsVisibility(state: SliderState): void {
  Object.entries(state.availableDirections).forEach(([key, isAvailable]) => {
    const button = state.el.querySelector(
      `[data-slider-button][data-dir="${key}"]`
    ) as HTMLElement

    if (button) {
      if (isAvailable) {
        button.classList.remove('quest-slider__button_hidden')
      } else {
        button.classList.add('quest-slider__button_hidden')
      }
    }
  })
}

/**
 * Обновляет индекс активного слайда и состояние доступных направлений навигации.
 * Выполняет прокрутку к новому слайду и обновляет видимость кнопок навигации.
 * @param {SliderState} state - Состояние слайдера
 * @param {number} newIndex - Новый индекс слайда для отображения
 */
function updateSlideIndex(state: SliderState, newIndex: number): void {
  if (newIndex === state.activeSlideIndex) {
    return
  }

  state.slider.scrollTo(newIndex)
  state.activeSlideIndex = newIndex

  state.availableDirections = {
    prev: state.slider.canScrollPrev(),
    next: state.slider.canScrollNext()
  }

  updateButtonsVisibility(state)
}

/**
 * Инициализирует слайдер квеста на основе переданного HTML-элемента.
 * Создает экземпляр Embla Carousel, настраивает обработчики событий для навигации
 * и управляет состоянием слайдера (индекс, доступные направления).
 * @param {HTMLElement} el - HTML-элемент контейнера слайдера
 */
function initQuestSlider(el: HTMLElement): void {
  const state: SliderState = {
    el,
    slider: null as unknown as EmblaCarouselType,
    activeSlideIndex: 0,
    availableDirections: {
      prev: false,
      next: true
    }
  }

  // eslint-disable-next-line new-cap
  state.slider = EmblaCarousel(el, {
    align: 'start',
    loop: true,
    dragFree: true,
    startIndex: 0
  })

  state.slider.on('select', () => {
    const newIndex = state.slider.selectedScrollSnap()
    updateSlideIndex(state, newIndex)
  })

  el.addEventListener('click', (event: Event) => {
    const target = event.target as HTMLElement
    const button = target.closest('[data-slider-button]') as HTMLElement

    if (!button) {
      return
    }

    const delta = button.dataset.dir === 'next' ? 1 : -1
    updateSlideIndex(state, state.activeSlideIndex + delta)
  })

  updateButtonsVisibility(state)
}

/**
 * Инициализирует все слайдеры квестов на странице.
 * Находит все элементы с атрибутом `data-quest-slider` и инициализирует каждый из них.
 */
export function initQuestSliders(): void {
  const sliders = document.querySelectorAll('[data-quest-slider]')
  sliders.forEach((slider) => {
    initQuestSlider(slider as HTMLElement)
  })
}

export default initQuestSlider
